import './styles.scss';

import React from 'react';
import ReactSpeedometer from 'react-d3-speedometer';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Container } from '../../components/Container';
import { VisibleOnVersion } from '../../components/VisibleOnVersion';
import { useStoreData } from '../../hooks';
import { media } from '../../services/media';
import { parseHTML } from '../../services/parseHTML';
import { replaceShortcodes } from '../../services/replaceShortcodes';
import { themeStyles } from '../../services/themeStyles';
import { Storyblok, StoryblokSection } from '../../storyblok';

import { Bubble } from './Bubble';
import { Comparision } from './Comparision';

export const ScoreSummary = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ScoreSummary>) => {
    const { scoreData } = useStoreData().user;

    if (
        !sectionData ||
        !scoreData ||
        !scoreData.bestScore ||
        !scoreData.summaryHeadline
    ) {
        return null;
    }

    const year = Object.keys(scoreData.bestScore).reverse()[0];
    const score = scoreData.bestScore[year];
    const headlineName = scoreData.summaryHeadline;

    const contentWithShortcode = (HTMLString: string) =>
        parseHTML(
            replaceShortcodes(HTMLString, {
                score: `<span class="score">${score}</span>`,
            }),
        );

    const {
        headlines,
        comparisions,
        bubble_content: bubbleContent,
        button_compare: buttonCompare,
    } = sectionData;

    const headline = headlines.find((h) => h.name === headlineName)?.text;

    return (
        <Section className="ScoreSummary">
            <Container sizeGroup="size-1">
                <div className="ScoreSummary-bubble">
                    <VisibleOnVersion versions={['upscore']}>
                        <Bubble
                            transition={false}
                            backgroundSvgName="bubbleTop"
                            centerContentHorizontally
                            centerContentVertically
                        >
                            {bubbleContent && (
                                <div className="ScoreSummary-bubble-content">
                                    {contentWithShortcode(bubbleContent)}
                                </div>
                            )}
                        </Bubble>
                    </VisibleOnVersion>

                    <VisibleOnVersion versions={['pengestyrke']}>
                        <div className="ScoreSummary-content">
                            {headline && (
                                <h2 className="">
                                    {contentWithShortcode(headline)}
                                </h2>
                            )}
                        </div>

                        <div className="ScoreSummary__columns">
                            <img src="assets/images/hallgeir.png" />
                            <ReactSpeedometer
                                width={500}
                                maxValue={1000}
                                value={score}
                                segments={4000}
                                maxSegmentLabels={5}
                                needleColor="#133143"
                                paddingVertical={25}
                                valueTextFontSize={'28px'}
                                needleTransitionDuration={1500}
                                currentValueText="Pengestyrke: ${value} av 1000"
                            />
                        </div>
                    </VisibleOnVersion>
                </div>

                <VisibleOnVersion versions={['upscore']}>
                    <div className="ScoreSummary-content">
                        {headline && (
                            <h2 className="">
                                {contentWithShortcode(headline)}
                            </h2>
                        )}
                    </div>
                </VisibleOnVersion>

                <div className="ScoreSummary-summary-wrapper">
                    <Comparision
                        scoreData={scoreData}
                        comparisions={comparisions}
                        buttonCompare={buttonCompare}
                    />
                </div>
            </Container>
        </Section>
    );
};

const Upscore = themeStyles(
    'upscore',
    css`
        max-width: 100%;

        margin: 0 auto 80px;

        ${media.min('md')} {
            max-width: 100%;
            min-height: 380px;
            padding-top: 30px;
        }
        ${media.min('lg')} {
            margin-bottom: 80px;
        }

        ${media.min('xl')} {
            min-height: 420px;
            padding-top: 50px;
        }

        ${media.min('xxl')} {
            margin-bottom: 80px;
            min-height: 750px;
            padding-top: 90px;
        }
    `,
);

const Pengestyrke = themeStyles(
    'pengestyrke',
    css`
        max-width: 100%;
        position: relative;

        max-width: 1200px;
        margin: 20px auto;
        padding: 20px 15px 10px;

        ${media.min('md')} {
            min-height: 380px;
            margin: 0 auto 50px;
            padding: 80px 15px 30px;
        }

        ${media.min('lg')} {
            margin-bottom: 50px;
        }

        ${media.min('xl')} {
            min-height: 420px;
        }

        ${media.min('xxl')} {
            margin-bottom: 90px;
            min-height: 750px;
        }
    `,
);

const Section = styled.section`
    ${Upscore}
    ${Pengestyrke}
`;
